import React, { useState, useEffect, useMemo } from 'react';
import Select from 'react-select';
import { useFetch } from '../hooks/useFetch';
import Loader from '../components/Loader';
import { useAuth } from '../contexts/AuthContext';
import SupplierHistoryTable from '../components/SupplierHistoryTable';
import { toast } from 'react-toastify';

const Mydebt = () => {
  const { token, user } = useAuth();
  const authHeaders = useMemo(() => ({
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    ...(user?.ownerId || user?.id ? { 'x-owner-id': String(user.ownerId || user.id) } : {}),
    ...(user?.staffId ? { 'x-staff-id': String(user.staffId) } : {}),
  }), [token, user]);

  const { data: suppliers, loading: loadingSuppliers } = useFetch('/api/suppliers', { headers: authHeaders });

  // Overall Balances State
  const [overallBalances, setOverallBalances] = useState([]);
  const [loadingBalances, setLoadingBalances] = useState(false);
  const [showBalanceModal, setShowBalanceModal] = useState(false);

  // Selection & Payment State
  const [selectedSupplierId, setSelectedSupplierId] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentNote, setPaymentNote] = useState('');
  const [submittingPayment, setSubmittingPayment] = useState(false);

  // Refresh Trigger for Table
  const [refreshHistory, setRefreshHistory] = useState(0);

  // Fetch balances using the new endpoint and merge with supplier names
  const fetchBalances = async () => {
    if (!suppliers) return;
    setLoadingBalances(true);
    try {
      const res = await fetch('/api/suppliers/balances', { headers: authHeaders });
      if (!res.ok) throw new Error('Failed to fetch balances');
      const balancesData = await res.json();

      // Merge with supplier details
      const merged = balancesData.map(b => {
        const supplier = suppliers.find(s => s.supplierid === b.supplierid);
        return {
          ...b,
          name: supplier ? supplier.name : 'Unknown Supplier',
          currency: b.currency || supplier?.currency || 'USD'
        };
      });
      setOverallBalances(merged);
    } catch (err) {
      console.error(err);
      toast.error('Balanslarni yuklashda xatolik');
    } finally {
      setLoadingBalances(false);
    }
  };

  useEffect(() => {
    if (suppliers) {
      fetchBalances();
    }
  }, [suppliers, refreshHistory]); // Refresh balances when history updates (purchase/payment)

  // Calculate top-level Balance Summary string
  const getBalanceSummary = () => {
    const totals = {};
    overallBalances.forEach(b => {
      const curr = b.currency || 'USD';
      totals[curr] = (totals[curr] || 0) + Number(b.balance);
    });

    const parts = Object.entries(totals).map(([curr, val]) => `${Number(val).toLocaleString()} ${curr}`);
    return parts.length > 0 ? parts.join(' | ') : '0';
  };

  const isOverallDebt = overallBalances.some(b => Number(b.balance) < 0);

  const supplierOptions = suppliers ? suppliers.map(s => ({ value: s.supplierid, label: s.name })) : [];

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    if (!selectedSupplierId || !paymentAmount) {
      toast.warning('Taminlovchi va summa kiritish shart');
      return;
    }

    setSubmittingPayment(true);
    try {
      const res = await fetch('/api/supplier-payments', {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({
          supplier_id: selectedSupplierId,
          amount: paymentAmount,
          note: paymentNote
        })
      });

      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.error || 'Payment failed');
      }

      toast.success('To\'lov muvaffaqiyatli amalga oshirildi');
      setPaymentAmount('');
      setPaymentNote('');
      setRefreshHistory(prev => prev + 1); // Trigger refresh of table and balances
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmittingPayment(false);
    }
  };

  const selectedSupplierBalance = overallBalances.find(b => b.supplierid === selectedSupplierId);

  return (
    <div className="container-fluid p-3">
      {/* Top Header with Balance */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1>Mening Qarzlarim</h1>
        <button
          className={`btn btn-lg ${isOverallDebt ? 'btn-danger' : 'btn-success'} shadow-sm`}
          style={{ borderRadius: '12px', padding: '12px 24px' }}
          onClick={() => setShowBalanceModal(true)}
        >
          {loadingBalances ? <Loader size="sm" /> : `Umumiy Balans: ${getBalanceSummary()}`}
        </button>
      </div>

      <div className="row">
        {/* Top: Payment Form (Horizontal) */}
        <div className="col-12 mb-4">
          <div className="card shadow-sm border-0" style={{ borderRadius: '15px' }}>
            <div className="card-body p-4">
              <h5 className="mb-4">Yangi to'lov</h5>
              <form onSubmit={handlePaymentSubmit}>
                <div className="row align-items-end">
                  <div className="col-md-3 mb-3 mb-md-0">
                    <label className="form-label text-muted small fw-bold">TAMINLOVCHI</label>
                    <Select
                      options={supplierOptions}
                      value={supplierOptions.find(o => o.value === selectedSupplierId)}
                      onChange={opt => setSelectedSupplierId(opt ? opt.value : null)}
                      placeholder="Tanlang..."
                      isLoading={loadingSuppliers}
                      styles={{
                        control: (base) => ({
                          ...base,
                          borderRadius: '10px',
                          border: '1px solid #eee',
                          padding: '2px'
                        })
                      }}
                    />
                  </div>

                  <div className="col-md-3 mb-3 mb-md-0">
                    <label className="form-label text-muted small fw-bold">SUMMA</label>
                    <input
                      type="number"
                      className="form-control"
                      style={{ borderRadius: '10px', border: '1px solid #eee', padding: '10px' }}
                      value={paymentAmount}
                      onChange={e => setPaymentAmount(e.target.value)}
                      placeholder="0.00"
                      required
                    />
                  </div>

                  <div className="col-md-4 mb-3 mb-md-0">
                    <label className="form-label text-muted small fw-bold">IZOH</label>
                    <input
                      type="text"
                      className="form-control"
                      style={{ borderRadius: '10px', border: '1px solid #eee', padding: '10px' }}
                      value={paymentNote}
                      onChange={e => setPaymentNote(e.target.value)}
                      placeholder="To'lov haqida izoh..."
                    />
                  </div>

                  <div className="col-md-2">
                    <button
                      type="submit"
                      className="btn btn-primary w-100 shadow-sm"
                      style={{ borderRadius: '10px', padding: '10px' }}
                      disabled={submittingPayment || !selectedSupplierId}
                    >
                      {submittingPayment ? <Loader size="sm" /> : 'To\'lov'}
                    </button>
                  </div>
                </div>

                {selectedSupplierId && (
                  <div className="mt-3 text-muted small">
                    Hozirgi balans: <span className={`fw-bold ${Number(selectedSupplierBalance?.balance || 0) < 0 ? 'text-danger' : 'text-success'}`}>{selectedSupplierBalance ? Number(selectedSupplierBalance.balance).toLocaleString() : '0'} {selectedSupplierBalance?.currency}</span>
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>

        {/* Bottom: History Table (Full Width) */}
        <div className="col-12">
          <div className="card shadow-sm border-0" style={{ borderRadius: '15px' }}>
            <div className="card-header bg-white py-3 border-0 d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Amallar Tarixi</h5>
              {selectedSupplierId && (
                <span className="badge bg-light text-dark shadow-sm px-3 py-2" style={{ borderRadius: '10px' }}>
                  {suppliers?.find(s => s.supplierid === selectedSupplierId)?.name}
                </span>
              )}
            </div>
            <div className="card-body p-0">
              {selectedSupplierId ? (
                <SupplierHistoryTable
                  supplierId={selectedSupplierId}
                  refreshTrigger={refreshHistory}
                />
              ) : (
                <div className="text-center text-muted py-5">
                  <img src="https://cdni.iconscout.com/illustration/premium/thumb/empty-cart-2130356-1800917.png" alt="Select supplier" style={{ opacity: 0.5, height: '150px' }} />
                  <p className="mt-3">Taminlovchini tanlab tarixingizni ko'ring</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Balance Modal */}
      {showBalanceModal && (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content border-0" style={{ borderRadius: '20px' }}>
              <div className="modal-header border-0 p-4">
                <h5 className="modal-title fw-bold">Barcha Taminlovchilar Balansi</h5>
                <button className="btn-close" onClick={() => setShowBalanceModal(false)}></button>
              </div>
              <div className="modal-body p-4 pt-0">
                {loadingBalances ? <Loader /> : (
                  <table className="table table-borderless align-middle">
                    <thead className="text-muted border-bottom">
                      <tr>
                        <th>#</th>
                        <th>Taminlovchi</th>
                        <th className="text-end">Balans</th>
                        <th className="text-end">Valyuta</th>
                      </tr>
                    </thead>
                    <tbody>
                      {overallBalances.map((b, idx) => (
                        <tr key={b.supplierid || idx} className="border-bottom" style={{ borderBottomColor: '#f8f9fa' }}>
                          <td className="text-muted">{idx + 1}</td>
                          <td className="fw-bold">{b.name}</td>
                          <td className={`text-end fw-bold ${Number(b.balance) < 0 ? 'text-danger' : 'text-success'}`}>
                            {Number(b.balance).toLocaleString()}
                          </td>
                          <td className="text-end text-muted small">{b.currency}</td>
                        </tr>
                      ))}
                      {overallBalances.length === 0 && (
                        <tr><td colSpan="4" className="text-center py-5 text-muted">Ma'lumot yo'q</td></tr>
                      )}
                    </tbody>
                  </table>
                )}
              </div>
              <div className="modal-footer border-0 p-4">
                <button className="btn btn-light w-100 py-2" style={{ borderRadius: '10px' }} onClick={() => setShowBalanceModal(false)}>Yopish</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Mydebt;